import React, { useEffect, useRef } from 'react';
import { CircularProgress } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { integrationsApi, useInitTodoIntegration } from '../../../data-access';
import './TickTickAuthCallback.scss';
import { Path } from '../../../routing/path';
import { getTodoOAuthExchangeRedirectUrl, trackEventMixpanel } from '../../../utils';

interface ExchangeCodeRes {
  accessToken: string;
}

const TickTickAuthCallback: React.FC = () => {
  const { initTodoIntegration } = useInitTodoIntegration();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hasExchanged = useRef(false);

  useEffect(() => {
    const exchange = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');

      if (!code || !state) {
        navigate(Path.DASHBOARD);
        return;
      }

      // Prevent multiple calls
      if (hasExchanged.current) return;
      hasExchanged.current = true;

      const params = new URLSearchParams(new URL(state).search);
      const redirectTo = params.get('redirectTo') ?? Path.DASHBOARD;
      const userId = params.get('userId');
      const isMobileApp = params.get('isMobileApp')?.includes('true');

      try {
        if (!userId) {
          console.error('No userId found in redirect URL');
          navigate(redirectTo);
          return;
        }

        const { data } = await integrationsApi.exchangeCode(
          code,
          'ticktick',
          getTodoOAuthExchangeRedirectUrl(Path.CALLBACK.TICKTICK_CALLBACK, searchParams)
        );

        if (!data) {
          navigate(redirectTo);
          return;
        }

        const accessToken = (data as ExchangeCodeRes).accessToken;

        const integrationId = await initTodoIntegration({
          token: accessToken,
          todoType: 'ticktick',
          userId,
          shouldSyncTasks: true,
          skipRefresh: isMobileApp,
        });

        trackEventMixpanel('connect_todo_auth_done', {
          todoType: 'ticktick',
          fromMobileApp: isMobileApp,
        });

        // Redirect to mobile app if the request is from a mobile app
        if (isMobileApp) {
          window.location.href = 'lifestack://todo-callback';
        } else {
          navigate(redirectTo || Path.DASHBOARD);
        }
      } catch (error) {
        console.error('Error during TickTick authentication:', error);
        navigate(redirectTo);
      }
    };

    exchange();
  }, [searchParams, navigate]);

  return (
    <div className="ticktick-auth-callback">
      <CircularProgress />
    </div>
  );
};

export default TickTickAuthCallback;
